import type { Task, TablesInsert, TablesUpdate } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

/** A task as Grid shows it: every column but the search vector, plus its label ids. */
export type GridTask = Omit<Task, 'search'> & { label_ids: string[] };

const TASK_COLUMNS =
  'id, game_id, parent_id, title, description, status, priority, assignee_id, start_date, due_date, estimate_hours, position, completed_at, sprint_id, source, source_id, import_meta, created_at, updated_at, created_by, task_labels(label_id)';

// PostgREST returns at most 1,000 rows per request; imported history can be longer.
const PAGE = 1000;

type TaskRow = Omit<GridTask, 'label_ids'> & { task_labels: { label_id: string }[] };

function toGridTask({ task_labels, ...task }: TaskRow): GridTask {
  return { ...task, label_ids: task_labels.map((l) => l.label_id) };
}

/** All of a game's tasks, subtasks included, in position order. */
export function useGameTasks(gameId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gameTasks(gameId ?? ''),
    enabled: !!gameId,
    queryFn: async (): Promise<GridTask[]> => {
      const all: GridTask[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase()
          .from('tasks')
          .select(TASK_COLUMNS)
          .eq('game_id', gameId!)
          .order('position')
          .order('id')
          .range(from, from + PAGE - 1);
        if (error) throw error;
        all.push(...(data as TaskRow[]).map(toGridTask));
        if (data.length < PAGE) return all;
      }
    },
  });
}

export type NewTask = Omit<TablesInsert<'tasks'>, 'search' | 'created_by' | 'source' | 'source_id' | 'import_meta'> & {
  label_ids?: string[];
};

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ label_ids = [], ...task }: NewTask) => {
      const client = supabase();
      const { data, error } = await client.from('tasks').insert(task).select('id, game_id').single();
      if (error) throw error;
      if (label_ids.length > 0) {
        const { error: labelError } = await client
          .from('task_labels')
          .insert(label_ids.map((label_id) => ({ task_id: data.id, label_id })));
        if (labelError) throw labelError;
      }
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
  });
}

export type TaskPatch = Omit<TablesUpdate<'tasks'>, 'id' | 'search' | 'created_by' | 'created_at'>;

/** Updates a task, showing the change at once and rolling back if the save fails. */
export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; gameId: string; patch: TaskPatch }) => {
      const { error } = await supabase().from('tasks').update(patch).eq('id', id);
      if (error) throw error;
    },
    onMutate: async ({ id, gameId, patch }) => {
      const key = queryKeys.gameTasks(gameId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GridTask[]>(key);
      queryClient.setQueryData<GridTask[]>(key, (tasks) =>
        tasks?.map((t) => (t.id === id ? ({ ...t, ...patch } as GridTask) : t)),
      );
      return { key, previous };
    },
    onError: (_error, _vars, context) => {
      if (context) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activity });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
  });
}

/** Adds or removes one label on a task. */
export function useToggleTaskLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, labelId, on }: { taskId: string; labelId: string; on: boolean }) => {
      const client = supabase();
      const { error } = on
        ? await client.from('task_labels').insert({ task_id: taskId, label_id: labelId })
        : await client.from('task_labels').delete().eq('task_id', taskId).eq('label_id', labelId);
      if (error) throw error;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activity });
    },
  });
}
